import {
  Component, ChangeDetectorRef, ElementRef, EventEmitter, HostBinding,
  Input, Output, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild,
  Renderer2, Inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  AbstractControl, AsyncValidatorFn, UntypedFormArray, UntypedFormBuilder, UntypedFormControl,
  UntypedFormGroup, Validators, ValidatorFn,
} from '@angular/forms';
import { Observable } from 'rxjs';


import * as _ from 'lodash';

import { prettifyNumber } from 'utils/prettify';
import { SortModel } from './list-manager-sort.model';
import { ListManagerModel } from './list-manager.model';
import { TableEditableComponent } from '../table/table-editable/table-editable.component';
import { DeleteModalComponent } from '../modal/delete-modal/delete-modal.component';
import { HiddenScrollComponent } from '../scroll/hidden-scroll/hidden-scroll.component';
import { ListManagerService } from './list-manager.service';
import {first, takeWhile} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Component({
  moduleId: module.id,
  selector: 'list-manager',
  templateUrl: 'list-manager.component.html',
  styleUrls: ['list-manager.component.scss'],
})
export class ListManagerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() title: string = '----';

  @Input() data: Array<ListManagerModel>;

  @Input() obsData: Observable<Array<ListManagerModel>>;

  @Input() show: boolean = false;

  @Input() hideEditControls: boolean = false;

  @Output() showChange: EventEmitter<boolean> = new EventEmitter();

  @Output() confirmChangesEvent: EventEmitter<any> = new EventEmitter();

  @Output() newListCreatedEvent: EventEmitter<any> = new EventEmitter();

  @Output() addToListCompletedEvent: EventEmitter<Array<ListManagerModel>> = new EventEmitter();

  @Output() addToListCancelledEvent: EventEmitter<any> = new EventEmitter();

  @Output() listSelectedEvent: EventEmitter<any> = new EventEmitter();

  @ViewChild(TableEditableComponent, {static: false}) tableEditable: TableEditableComponent | void;

  @ViewChild(DeleteModalComponent, {static: false}) deleteModal: DeleteModalComponent | void;

  @ViewChild('createNewListInput', {static: false}) createNewListInput: ElementRef | void;

  @ViewChild(HiddenScrollComponent, {static: false}) hiddenScroll: HiddenScrollComponent | void;

  searchTermControl = new UntypedFormControl();
  createNewListControl: UntypedFormControl;
  listsForm: UntypedFormGroup;
  sortModel: SortModel;
  selectedList: ListManagerModel;
  creatingNewList: boolean = false;

  @HostBinding('class.adding-to-list')
  addingToList: boolean = false;

  private sourceData: Array<ListManagerModel>;
  private isActive: boolean = true;

  @HostBinding('class.editing')
  get editing(): boolean {
    return this.tableEditable && this.tableEditable.isEditing;
  }

  get list(): UntypedFormArray {
    return this.listsForm.get('list') as UntypedFormArray;
  }

  get listControls(): Array<AbstractControl> {
    if (this.sortModel) {
      const order: Array<boolean | 'asc' | 'desc'> = this.sortModel.sort === 'asc' ? ['asc'] : ['desc'];
      return _.orderBy(this.filterControlsByName(this.list.controls), (item: any) => {
        return item.get(this.sortModel.column).value;
      }, order) as Array<AbstractControl>;
    } else {
      return this.filterControlsByName(this.list.controls) as Array<AbstractControl>;
    }
  }

  get selectedRowsLength(): number {
    return this.tableEditable && this.tableEditable.selectedRowsLength || 0;
  }

  get listsForUpdate(): Array<any> {
    return _.chain(this.list.controls)
    .filter((item: UntypedFormGroup) => {
      return item.get('name').dirty;
    })
    .map((item: UntypedFormGroup) => {
      return item.value;
    }).value();
  }

  constructor(
    protected fb: UntypedFormBuilder,
    protected listManagerService: ListManagerService,
    protected ref: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: any,
    protected renderer: Renderer2,
  ) {
    this.listsForm = this.fb.group({
      list: this.fb.array([])
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue !== changes.data.previousValue) {
      this.setData(changes.data.currentValue);
    }
    if (changes.show) {
      this.toggleClass();
    }
  }

  ngOnInit() {
    this.createNewListControl = new UntypedFormControl(
      '',
      [this.uniqueValidator().bind(this)],
    );
    if (this.obsData) {
      this.subscribeObsData();
    }
  }

  ngOnDestroy() {
    this.isActive = false;
    this.selectedList = null;
    this.sourceData = null;
    this.listsForm = null;
    this.show = false;
    this.toggleClass();
  }

  // public
  public addToList(): void {
    if (!this.show) {
      this.toggleListManager(true);
    }
    this.addingToList = true;
  }

  public showListManager(): void {
    this.toggleListManager(true);
  }

  public hideListManager(): void {
    this.toggleListManager(false);
  }

  public toggleListManager(toggle: boolean): void {
    this.show = toggle !== undefined ? toggle : !this.show;
    this.toggleClass();
    this.showChange.emit(this.show);
  }

  public resetSelectedLists(): void {
    _.each(this.list.controls, (formGroup: UntypedFormGroup) => {
      if (formGroup.get('selected')) {
        formGroup.controls.selected.setValue(false);
      }
    });
    this.selectedList = null;
  }

  public unselectList(id: any): void {
    const selected = _.find(this.list.controls, (formGroup: UntypedFormGroup) => {
      return formGroup.get('_id').value === id;
    });
    if (!selected) { return; }
    selected.get('selected').setValue(false);
    this.selectedList = null;
  }

  public addNewList(list: any): void {
    const controls = this.getListControlsGroup(list._id, list.name, list.count, this.addingToList);
    this.resetSelectedLists();
    this.list.insert(0, controls);
    if (this.addingToList) { this.selectedList = controls.value; }
    this.sourceData = this.list.value;
    this.ref.markForCheck();
  }

  public updateScroll(): void {
    if (this.hiddenScroll instanceof HiddenScrollComponent) {
      this.hiddenScroll.defineContentWidth();
    }
  }

  // handlers
  onEditClick() {
    if (this.tableEditable) {
      this.tableEditable.startEdit();
    }
  }

  onCancelClick() {
    // cancel adding to list action
    if (this.addingToList) {
      this.addingToList = false;
      this.resetSelectedLists();
      this.addToListCancelledEvent.emit();
      return;
    }
    // reset selected rows
    if (this.selectedRowsLength) {
      if (this.tableEditable) {
        this.tableEditable.resetSelected();
      }
    } else {
      this.setData(this.sourceData);
      this.listsForm.markAsPristine();
      if (this.tableEditable) {
        this.tableEditable.cancel();
      }
    }
  }

  onConfirmClick() {
    if (this.list.invalid) { return; }
    const requests = [];
    if (this.listsForUpdate.length) {
      requests.push(this.listManagerService.updateBunch(this.listsForUpdate));
    }
    forkJoin(requests)
    .pipe(first())
    .subscribe((res) => {
      this.updateListItemsCallback();
    }, (error: any) => {
      console.log(error);
    });
  }

  onCompleteClick() {
    const selectedLists = _.filter(<Array<ListManagerModel>>this.list.value, ['selected', true]);
    if (!selectedLists.length) { return; }
    this.addToListCompletedEvent.emit(selectedLists);
    this.addingToList = false;
    this.resetSelectedLists();
  }

  onRemoveSelectedClick() {
    if (this.deleteModal instanceof DeleteModalComponent) {
      this.deleteModal.show();
    }
  }

  onSortClick(column: string) {
    if (this.sortModel && this.sortModel.column === column) {
      this.sortModel.sort = this.sortModel.sort === 'asc' ? 'desc' : 'asc';
    } else {
      const sortModel = new SortModel();
      sortModel.column = column;
      sortModel.sort = 'asc';
      this.sortModel = sortModel;
    }
  }

  onCreatingNewListClick() {
    this.creatingNewList = true;
    this.focusCreateListInput();
  }

  onCancelCreatingClick() {
    this.creatingNewList = false;
  }

  onCreateNewListClick() {
    if (!this.createNewListControl.value || this.createNewListControl.invalid) {
      this.focusCreateListInput();
      return;
    }
    this.listManagerService.createNewList(this.createNewListControl.value)
    .subscribe((data: any) => {
      if (!data.success) {
        this.createNewListControl.setErrors({
          'available': data.message,
        });
      } else {
        this.addNewList(data.data_list);
        this.newListCreatedEvent.emit(data.data_list);
        this.creatingNewList = false;
        this.createNewListControl.reset('', {emitEvent: false});
        this.ref.markForCheck();
      }
    });
  }

  onListItemClick(listItem: UntypedFormGroup) {
    if (this.editing) { return; }
    const control = listItem.get('selected');
    if (this.addingToList) {
      control.setValue(!control.value);
    } else {
      const selectedList: any = _.find(this.list.controls, (item: UntypedFormGroup) => {
        return item.value.selected;
      });
      if (selectedList && listItem.value._id !== this.selectedList._id) {
        selectedList.get('selected').setValue(false);
      }
      control.setValue(!control.value);
      this.selectedList = listItem.value;
      this.listSelectedEvent.emit(this.selectedList);
    }
  }

  onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      const idsForRemove = _.map(this.tableEditable && this.tableEditable.selectedRows || [], (item: any) => {
        return item.row.get('_id').value;
      });
      if (this.tableEditable) {
        this.tableEditable.removeSelected();
      }
      this.listManagerService.removeBunch(idsForRemove)
      .pipe(first())
      .subscribe(() => {
        this.updateListItemsCallback();
      });
    } else {
      if (this.deleteModal instanceof DeleteModalComponent) {
        this.deleteModal.hide();
      }
    }
  }

  // private
  private setListsFormArray(data: Array<any> = []): void {
    if (!_.isArray(data)) { return; }
    const selectedId = this.selectedList && this.selectedList._id;
    const formGroups = _.map(data, (item: any) => {
      const selected = selectedId === item._id;
      return this.getListControlsGroup(item._id, item.name, item.count, selected);
    });
    const formArray = this.fb.array(formGroups);
    this.listsForm.setControl('list', formArray);
  }

  private getListControlsGroup(
    _id: any,
    name: string = '',
    count: number = 0,
    selected: boolean = false
  ): UntypedFormGroup {
    return this.fb.group({
      _id: new UntypedFormControl(_id),
      name: new UntypedFormControl(name, [this.uniqueValidator().bind(this)]),
      count: new UntypedFormControl(count),
      selected: new UntypedFormControl(selected)
    });
  }

  private setData(data: any = []): void {
    this.sourceData = data;
    this.data = data;
    this.setListsFormArray(data);
  }

  private prettifyNumber(value: Number): String {
    return prettifyNumber(value, ',');
  }

  private isSortItemArrowUp(column: string): boolean {
    if (!this.sortModel) { return false; }
    return this.sortModel.column === column && this.sortModel.sort === 'asc';
  }

  private isSortItemActive(column: string): boolean {
    if (!this.sortModel) { return false; }
    return this.sortModel.column === column;
  }

  private subscribeObsData(): void {
    this.obsData
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      this.setData(data);
    });
  }

  private uniqueValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} => {
      const hasIdentical = _.some(this.list.controls, (item: UntypedFormGroup) => {
        const nameControl = item.get('name');
        return nameControl !== control && nameControl.valid && nameControl.value === control.value;
      });
      return hasIdentical ? {'unique': 'List name is not unique!'} : null;
    };
  }

  private filterControlsByName(controls: Array<AbstractControl>): Array<AbstractControl> {
    if (!this.searchTermControl.value) { return controls; }
    return _.filter(controls, (item: AbstractControl) => {
      return item.get('name').value
      .toLowerCase().indexOf(this.searchTermControl.value.toLowerCase()) !== -1;
    });
  }

  private focusCreateListInput(): void {
    _.defer(() => this.createNewListInput && this.createNewListInput.nativeElement.focus());
  }

  private updateListItemsCallback(): void {
    this.confirmChangesEvent.emit(this.list.value);
    if (this.tableEditable) {
      this.tableEditable.cancel();
    }
    this.listsForm.markAsPristine();
    this.sourceData = this.list.value;
  }

  private toggleClass() {
    if (this.renderer) {
      if (this.show) {
        this.renderer.addClass(document.documentElement, 'list-manager-open');
      } else {
        this.renderer.removeClass(document.documentElement, 'list-manager-open');
      }
    }
  }
}
